import { useEffect, useState, useCallback, useMemo } from "react";
import { useMsal } from "@azure/msal-react";
import { CheckCircle, Circle } from "lucide-react";
import { callApi } from "../api/api";
import { notifyAuthContextChanged } from "../utils/authCache";
import ResponsiveTableCards from "../components/ResponsiveTableCards";
import ScrollableTable from "../components/ScrollableTable";

const isRoleActive = (role) =>
  role?.is_active !== false && role?.is_active !== 0;

const isRoleAssignmentFrozen = (role) => !isRoleActive(role);

const getRoleStatusLabel = (role) =>
  isRoleActive(role) ? "Active" : "Inactive";

const FROZEN_ROLE_TOOLTIP =
  "Role is inactive. Enable the role to modify assignments.";

export default function AssignUserRole() {
  const { instance, accounts } = useMsal();


  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isNarrow, setIsNarrow] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 960px)");
    const update = () => setIsNarrow(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  //const [assignedRoles, setAssignedRoles] = useState({});
  const [draftRoles, setDraftRoles] = useState({});
  const [initialRoles, setInitialRoles] = useState({});

  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [toast, setToast] = useState(null);

  /* ================= TOAST ================= */
  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  /* ================= LOAD USERS + ROLES ================= */
  const loadData = useCallback(async () => {
    if (!accounts.length) return;

    try {
      setLoading(true);

      const [usersRes, rolesRes] = await Promise.all([
        callApi(instance, accounts, "GET", "/api/admin/users"),
        callApi(instance, accounts, "GET", "/api/admin/roles"),
      ]);

      if (!usersRes || usersRes?.data?.success === false) {
        throw new Error("Users load failed");
      }

      if (!rolesRes || rolesRes?.data?.success === false) {
        throw new Error("Roles load failed");
      }

      const userList = usersRes.data.data || [];
      const roleList = rolesRes.data.data || [];

      setUsers(userList);
      setRoles(roleList);

      if (userList.length) {
        setSelectedUser(userList[0]);
      }
    } catch (err) {
      console.error(err);
      showToast(err.message, "error");
    } finally {
      setLoading(false);
    }
  }, [instance, accounts]);

  /* ================= LOAD USER ROLES ================= */
  const loadUserRoles = useCallback(
    async (userId) => {
      try {
        //setAssignedRoles({});
        setDraftRoles({});
        setInitialRoles({});

        const res = await callApi(
          instance,
          accounts,
          "GET",
          `/api/user/user-roles?userId=${userId}`,
        );

        if (!res || res?.data?.success === false) {
          throw new Error("Failed to load user roles");
        }

        const map = {};

        (res.data.data || []).forEach((r) => {
          const id = r.role_id || r.roleId;
          if (id) map[id] = true;
        });

        // setAssignedRoles(map);
        setDraftRoles(map);
        setInitialRoles(map);
      } catch (err) {
        console.error(err);
        showToast("Failed to load roles", "error");
      }
    },
    [instance, accounts],
  );

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (selectedUser) {
      loadUserRoles(selectedUser.user_id);
    }
  }, [selectedUser, loadUserRoles]);

  /* ================= TOGGLE ================= */
  const toggleRole = useCallback((roleId) => {
    const role = roles.find((item) => item.role_id === roleId);
    if (role && isRoleAssignmentFrozen(role)) {
      return;
    }

    setDraftRoles((prev) => ({
      ...prev,
      [roleId]: !prev[roleId],
    }));
  }, [roles]);

  /* ================= SAVE ================= */
  const handleSave = async () => {
    if (!selectedUser) return;

    try {
      setToggling(true);

      const toAssign = [];
      const toRemove = [];

      roles.forEach((role) => {
        if (isRoleAssignmentFrozen(role)) {
          return;
        }

        const was = !!initialRoles[role.role_id];
        const now = !!draftRoles[role.role_id];

        if (!was && now) toAssign.push(role.role_id);
        if (was && !now) toRemove.push(role.role_id);
      });

      await Promise.all([
        ...toAssign.map((roleId) =>
          callApi(instance, accounts, "POST", "/api/admin/assign-role", {
            userId: selectedUser.user_id,
            roleId,
          }),
        ),
        ...toRemove.map((roleId) =>
          callApi(instance, accounts, "POST", "/api/admin/remove-user-role", {
            userId: selectedUser.user_id,
            roleId,
          }),
        ),
      ]);

      showToast("Roles updated successfully");
      notifyAuthContextChanged();

      await loadUserRoles(selectedUser.user_id);
    } catch (err) {
      console.error(err);
      showToast("Save failed", "error");
    } finally {
      setToggling(false);
    }
  };

  /* ================= FILTER ================= */
  const filteredUsers = useMemo(() => {
    return users.filter((u) =>
      u.email_address?.toLowerCase().includes(search.toLowerCase()),
    );
  }, [users, search]);

  /* ================= RESPONSIVE CARD MAPPERS ================= */

  const mapRoleCard = useCallback(
    (role) => {
      const assigned = !!draftRoles[role.role_id];
      const statusLabel = getRoleStatusLabel(role);
      const frozen = isRoleAssignmentFrozen(role);

      return {
        id: `role-${role.role_id}`,
        icon: assigned ? <CheckCircle size={16} /> : <Circle size={16} />,
        tone: assigned ? "success" : undefined,
        title: role.role_name,
        subtitle: role.role_code,
        meta: [{ label: "Status", value: statusLabel }],
        badges: [
          {
            value: assigned ? "Assigned" : "Unassigned",
            tone: assigned ? "success" : undefined,
          },
          ...(frozen
            ? [{ value: "Assignments frozen", tone: "warning" }]
            : []),
        ],
        actions: frozen
          ? []
          : [
              {
                label: assigned ? "Unassign" : "Assign",
                icon: assigned ? <CheckCircle size={14} /> : <Circle size={14} />,
                variant: assigned ? "secondary" : undefined,
                onClick: () => toggleRole(role.role_id),
              },
            ],
      };
    },
    [draftRoles, toggleRole],
  );

  const mapRoleDrawer = useCallback(
    (role) => {
      const assigned = !!draftRoles[role.role_id];
      const frozen = isRoleAssignmentFrozen(role);

      return {
        title: role.role_name,
        fields: [
          { label: "Role Code", value: role.role_code || "—" },
          { label: "Status", value: getRoleStatusLabel(role) },
          { label: "Assignment", value: assigned ? "Assigned" : "Not Assigned" },
          ...(frozen
            ? [
                {
                  label: "Note",
                  value: "Assignments are frozen while this role is inactive.",
                },
              ]
            : []),
        ],
        primaryAction: frozen
          ? undefined
          : {
              label: assigned ? "Remove Role" : "Assign Role",
              onClick: () => toggleRole(role.role_id),
            },
      };
    },
    [draftRoles, toggleRole],
  );

  const renderRolesTable = () => (
    <ScrollableTable style={styles.tableWrap}>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.thCenter}>Assigned</th>
            <th style={styles.th}>Role</th>
            <th style={styles.th}>Code</th>
            <th style={styles.th}>Status</th>
          </tr>
        </thead>

        <tbody>
          {roles.map((role) => {
            const frozen = isRoleAssignmentFrozen(role);

            return (
            <tr
              key={role.role_id}
              style={{
                ...styles.row,
                ...(frozen ? styles.frozenRow : null),
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = "#f8fafc")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = frozen ? "#fafafa" : "#fff")
              }
            >
              <td style={styles.tdCenter}>
                <input
                  type="checkbox"
                  style={{
                    ...styles.checkbox,
                    ...(frozen ? styles.checkboxDisabled : null),
                  }}
                  checked={!!draftRoles[role.role_id]}
                  disabled={frozen}
                  title={frozen ? FROZEN_ROLE_TOOLTIP : undefined}
                  onChange={() => toggleRole(role.role_id)}
                />
              </td>

              <td style={styles.td}>
                <div style={styles.roleName}>{role.role_name}</div>
              </td>

              <td style={styles.td}>
                <span style={styles.tdCode}>{role.role_code}</span>
              </td>

              <td style={styles.td}>
                <span
                  style={
                    isRoleActive(role) ? styles.active : styles.inactive
                  }
                >
                  ● {getRoleStatusLabel(role)}
                </span>
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
    </ScrollableTable>
  );

  if (loading) return <div className="page-container" style={styles.container}>Loading...</div>;

  return (
    <div className="page-container" style={{ ...styles.container, ...(isNarrow ? styles.containerNarrow : null) }}>
      <div style={styles.title}>
        <h2>User-Role Assignments</h2>
        <p style={styles.subtitle}>
          Assign and manage role memberships for portal users.
        </p>
      </div>

      <div style={{ ...styles.layout, ...(isNarrow ? styles.layoutNarrow : null) }}>
        {/* LEFT */}
        <div style={{ ...styles.left, ...(isNarrow ? styles.leftNarrow : null) }}>
          <input
            placeholder="Search users by email..."
            style={styles.search}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <div style={styles.userList} className="scrollable">
            {filteredUsers.map((user) => (
              <div
                key={user.user_id}
                style={{
                  ...styles.userItem,
                  ...(selectedUser?.user_id === user.user_id
                    ? styles.userSelected
                    : styles.userNormal),
                }}
                onClick={() => setSelectedUser(user)}
              >
                <div>
                  <div style={styles.userName}>
                    {user.first_name || user.email_address?.split("@")[0]}
                  </div>

                  <div style={styles.email}>{user.email_address}</div>
                </div>

                <span style={styles.count}>{user.role_count || 0}</span>
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT */}
        <div style={{ ...styles.right, ...(isNarrow ? styles.rightNarrow : null) }}>
          {selectedUser && (
            <div style={styles.rightInner}>
              <div style={styles.selectedHeader}>
                <div style={styles.selectedTitle}>
                  {selectedUser.email_address}
                </div>

                <div style={styles.selectedSub}>
                  Configure assigned roles and permissions
                </div>
              </div>

              <div style={styles.tableArea}>
                <ResponsiveTableCards
                  breakpoint={960}
                  data={roles}
                  loading={false}
                  emptyTitle="No Roles Available"
                  emptySub="No roles have been configured yet."
                  listTitle="Available Roles"
                  listSubtitle={`Tap a role to toggle assignment for ${selectedUser.email_address}`}
                  cardMapper={mapRoleCard}
                  drawerMapper={mapRoleDrawer}
                  renderTable={renderRolesTable}
                />
              </div>

              <div style={styles.footer}>
                <button
                  style={{
                    ...styles.saveBtn,
                    opacity: toggling ? 0.7 : 1,
                    cursor: toggling ? "not-allowed" : "pointer",
                  }}
                  onMouseEnter={(e) =>
                    !toggling &&
                    (e.currentTarget.style.transform = "translateY(-1px)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.transform = "translateY(0)")
                  }
                  onClick={handleSave}
                  disabled={toggling}
                >
                  {toggling ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {toast && (
        <div
          style={{
            ...styles.toast,
            background: toast.type === "success" ? "#16a34a" : "#ef4444",
          }}
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    background: "#f8fafc",
    minHeight: "80vh",
  },

  containerNarrow: {
    padding: "12px",
  },

  title: {
    marginBottom: "24px",
  },

  subtitle: {
    color: "#64748b",
    marginTop: "4px",
    fontSize: "14px",
  },

  /* ================= LAYOUT ================= */

  layout: {
    display: "grid",
    gridTemplateColumns: "340px 1fr",
    gap: "24px",
    alignItems: "stretch",
  },

  layoutNarrow: {
    gridTemplateColumns: "1fr",
    gap: "16px",
  },

  /* ================= LEFT PANEL ================= */

  left: {
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: "18px",
    padding: "18px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
    height: "calc(100vh - 180px)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },

  leftNarrow: {
    height: "auto",
    maxHeight: "340px",
  },

  search: {
    width: "100%",
    padding: "12px 14px",
    borderRadius: "10px",
    border: "1px solid #dbe2ea",
    fontSize: "14px",
    outline: "none",
    marginBottom: "18px",
    boxSizing: "border-box",
    transition: "all 0.2s ease",
  },

  userList: {
    overflowY: "auto",
    overflowX: "hidden",
    flex: 1,
    paddingRight: "4px",
  },

  userItem: {
    padding: "14px 16px",
    marginBottom: "10px",
    borderRadius: "14px",
    cursor: "pointer",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    transition: "all 0.2s ease",
    border: "1px solid transparent",
  },

  userSelected: {
    background: "#eef2ff",
    border: "1px solid #c7d2fe",
  },

  userNormal: {
    background: "#fff",
    border: "1px solid #f1f5f9",
  },

  userName: {
    fontWeight: "600",
    color: "#0f172a",
    marginBottom: "4px",
    fontSize: "14px",
  },

  email: {
    fontSize: "12px",
    color: "#64748b",
    wordBreak: "break-word",
  },

  count: {
    background: "#e2e8f0",
    borderRadius: "999px",
    padding: "5px 10px",
    fontSize: "12px",
    fontWeight: "600",
    color: "#334155",
    minWidth: "30px",
    textAlign: "center",
  },

  /* ================= RIGHT PANEL ================= */

  right: {
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: "18px",
    padding: "24px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
    height: "calc(100vh - 180px)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },

  rightNarrow: {
    height: "auto",
  },

  rightInner: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minHeight: 0,
    height: "100%",
  },

  tableArea: {
    flex: 1,
    minHeight: 0,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },

  selectedHeader: {
    marginBottom: "24px",
    paddingBottom: "18px",
    borderBottom: "1px solid #f1f5f9",
    flexShrink: 0,
  },

  selectedTitle: {
    fontSize: "18px",
    fontWeight: "700",
    color: "#0f172a",
    marginBottom: "6px",
  },

  selectedSub: {
    fontSize: "13px",
    color: "#64748b",
  },

  /* ================= TABLE ================= */

  tableWrap: {
    overflowX: "auto",
  },

  table: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
  },

  th: {
    textAlign: "left",
    padding: "16px 18px",
    fontSize: "12px",
    fontWeight: "700",
    letterSpacing: "0.05em",
    color: "#64748b",
    background: "#f8fafc",
    borderBottom: "1px solid #e2e8f0",
    whiteSpace: "nowrap",
  },

  thCenter: {
    textAlign: "center",
    padding: "16px 18px",
    fontSize: "12px",
    fontWeight: "700",
    letterSpacing: "0.05em",
    color: "#64748b",
    background: "#f8fafc",
    borderBottom: "1px solid #e2e8f0",
    width: "120px",
  },

  row: {
    transition: "background 0.2s ease",
  },

  td: {
    padding: "18px",
    borderBottom: "1px solid #f8fafc",
    fontSize: "14px",
    verticalAlign: "middle",
  },

  tdCenter: {
    textAlign: "center",
    padding: "18px",
    borderBottom: "1px solid #f8fafc",
    verticalAlign: "middle",
  },

  roleName: {
    fontWeight: "600",
    color: "#0f172a",
  },

  tdCode: {
    fontFamily: "monospace",
    fontSize: "13px",
    color: "#334155",
  },

  active: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    color: "#15803d",
    background: "#dcfce7",
    padding: "6px 10px",
    borderRadius: "999px",
    fontSize: "12px",
    fontWeight: "600",
  },

  inactive: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    color: "#dc2626",
    background: "#fee2e2",
    padding: "6px 10px",
    borderRadius: "999px",
    fontSize: "12px",
    fontWeight: "600",
  },

  checkbox: {
    width: "18px",
    height: "18px",
    cursor: "pointer",
    accentColor: "#0f172a",
  },

  checkboxDisabled: {
    cursor: "not-allowed",
    opacity: 0.55,
  },

  frozenRow: {
    background: "#fafafa",
  },

  /* ================= FOOTER ================= */

  footer: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: "24px",
    paddingTop: "20px",
    borderTop: "1px solid #f1f5f9",
    flexShrink: 0,
  },

  saveBtn: {
    background: "#0f172a",
    color: "#fff",
    border: "none",
    padding: "12px 18px",
    borderRadius: "10px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.2s ease",
    minWidth: "160px",
  },

  /* ================= TOAST ================= */

  toast: {
    position: "fixed",
    bottom: "24px",
    right: "24px",
    padding: "14px 18px",
    borderRadius: "12px",
    color: "#fff",
    fontWeight: "500",
    boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
    zIndex: 1000,
  },
};
